import type { NextPage } from 'next'

import { PageLayout } from 'src/libs/layout'
import {
    Header,
    Heading2,
    Heading3,
    Paragraph,
    TextSection,
    ImageWithCaption,
} from 'src/libs/page-blog'

import image1Header from './assets/salesforce-experience/1-header-welcome.png'
import image2Salesforce from './assets/salesforce-experience/2-salesforce.png'
import image3LFWelcome from './assets/salesforce-experience/3-laserfocus-welcome.png'
import image4LFHome from './assets/salesforce-experience/4-home.png'
import image5LFOverdue from './assets/salesforce-experience/5-overdue.png'
import image6SFRecent from './assets/salesforce-experience/6-salesforce-recent.png'
import image7LFADP from './assets/salesforce-experience/7-laserfocus-detail-page.png'
import image8SFDetail from './assets/salesforce-experience/8-salesforce-detail-page.png'
import image9StackCard from './assets/salesforce-experience/9-pipeline-stack-card.png'
import image10ColumnSelection from './assets/salesforce-experience/10-column-selection.png'
import image11TableFilter from './assets/salesforce-experience/11-filter.png'
import image12Table from './assets/salesforce-experience/12-table.png'

const SalesforceExperience: NextPage = () => {
    return (
        <PageLayout>
            <Header
                title="How to give your team a better Salesforce experience without leaving Salesforce"
                metadata={{
                    duration: 6,
                    date: new Date(2021, 9, 20),
                }}
                image={{
                    src: image1Header,
                    alt: 'Laserfocus Welcome Screen',
                }}
            />

            <TextSection>
                <Paragraph>
                    Salesforce is the juggernaut in the CRM space. The company is expected to pull
                    in more than $21 billion in revenue this year alone. They serve more than
                    150,000 businesses. But all this success doesn’t mean the experience is
                    flawless.
                </Paragraph>
                <Paragraph>
                    Just search Google or social media for “Salesforce experience”. You’ll find
                    plenty of complaints, but as this Quora post from 2017 suggests, one reason
                    Salesforce’s experience continues down this path is that the software is often
                    sold through a top-down process.
                </Paragraph>
                <Paragraph>
                    This generally means that the Salesforce sales team is talking with executives
                    at the companies they are trying to sell to. The executives have boxes that need
                    to be checked, and Salesforce is more than happy to oblige them. Salesforce is
                    also designed to be customizable. When you combine the incredible number of
                    features and the almost limitless customizations available, Salesforce can
                    become unwieldy for many teams.
                </Paragraph>
                <Paragraph>
                    But what do you do if your team is required to use Salesforce? Just suck it up?
                    Until recently, that may have been the case, but with Laserfocus, you can layer
                    on a beautiful, productive, and simple experience, without leaving Salesforce.
                </Paragraph>
                <Heading2>Connecting Laserfocus</Heading2>
                <Paragraph>
                    Let’s take a look at how you can improve your overall Salesforce experience
                    while also improving your team’s productivity. I’ll be using a test Salesforce
                    account, which, to be honest, is already pretty scary to look at. Your account
                    probably looks similar, but with significantly more data.
                </Paragraph>
                <ImageWithCaption
                    src={image2Salesforce}
                    alt="Salesforce lead view"
                    caption="This is my leads page. Not so bad, right?"
                />
                <Paragraph>
                    Now, how do I find my upcoming tasks? How do I get back to my dashboard? What’s
                    the deal flow looking like? Many of you know the answers to these questions
                    because you’ve learned over time how to navigate the interface. That doesn’t
                    mean new employees will know how to find these things, and it doesn’t mean that
                    you like navigating the interface.
                </Paragraph>
                <Paragraph>
                    Let’s fix that. Head on over to Laserfocus and sign up for an account. The
                    process is easy because you’re signing up for an account using your existing
                    Salesforce account. This means that in addition to signing up for Laserfocus,
                    you are also linking it to your Salesforce data.
                </Paragraph>
                <ImageWithCaption
                    src={image3LFWelcome}
                    alt="Laserfocus welcome screen"
                    caption="Login securely via Salesforce Oauth"
                />
                <Paragraph>
                    Once you sign in with Salesforce, your free trial will start, and your data will
                    be imported. When the syncing process is complete, you’ll see a screen like
                    this:
                </Paragraph>
                <ImageWithCaption
                    src={image4LFHome}
                    alt="Laserfocus home screen"
                    caption="Isn’t that already a more inviting view?"
                />
                <Paragraph>
                    Each card on the screen is intentional. Each is clear. I know that I have 2
                    overdue tasks. I have 13 opportunities that are potentially worth over a million
                    dollars. I now feel like I’m in the driver’s seat rather than Salesforce being
                    in the driver’s seat. And yet, everything I do here syncs with Salesforce. Let’s
                    see what this looks like in practice.
                </Paragraph>
                <Heading2>Updating Laserfocus AND Salesforce</Heading2>
                <Paragraph>
                    We’ll use my two overdue tasks as an example. If I click on that card, I will
                    see the following:
                </Paragraph>
                <ImageWithCaption src={image5LFOverdue} alt="Overdue tasks in Laserfocus" />
                <Paragraph>
                    Now, before I do anything, let’s find these tasks in Salesforce. Back in the
                    Salesforce UI, I have to find the dropdown that lets me switch between various
                    views. If I select the “Tasks” view, I can see this:
                </Paragraph>
                <ImageWithCaption src={image6SFRecent} alt="Recently viewed tasks in Salesforce" />
                <Paragraph>
                    So, we know that the tasks in Salesforce match the tasks in Laserfocus. What
                    happens if I complete a task?
                </Paragraph>
                <Paragraph>
                    By clicking on an overdue task in Laserfocus, I get a detailed view. Here I
                    realize that both of my overdue tasks are tied to the same customer. So, let’s
                    pretend I give Barb a call. I note that call, and I complete the tasks.
                </Paragraph>
                <Heading3>Create a new follow-up.Here’s what Laserfocus looks like.</Heading3>
                <ImageWithCaption src={image7LFADP} alt="Account details in Laserfocus" />
                <Paragraph>
                    This is clean, efficient, and easy to understand. But if the rest of the company
                    is using Salesforce, we need to make sure that anything my team does is properly
                    reflected in Salesforce. Let’s take a look.
                </Paragraph>
                <Paragraph>
                    Back in Salesforce, if I click on the opportunity name in my task list, I can
                    get to a detailed view. This view shows that, yes, everything I’ve done in
                    Laserfocus is reflected in Salesforce:
                </Paragraph>
                <ImageWithCaption src={image8SFDetail} alt="Account details in Salesforce" />
                <Paragraph>
                    Perfect! We know that all updates are synced back and forth. Now, we can dive
                    into how to use Laserfocus to drive your day rather than letting your day drive
                    you.
                </Paragraph>
                <Heading2>The Anti-Salesforce Experience</Heading2>
                <Paragraph>
                    Time in a CRM should be minimal. You should be able to find your tasks, look up
                    specific info, and be on your way. Ideally, your day should be spent talking
                    with prospects, closing deals, and following up with big customers. Laserfocus
                    helps you do exactly that.
                </Paragraph>
                <Paragraph>
                    Using my example dashboard, I can see my opportunities are stacking up. That’s
                    all well and good, but I want to quickly find the best opportunities to follow
                    up on today.
                </Paragraph>
                <ImageWithCaption src={image9StackCard} alt="Laserfocus Pipeline Review Card" />
                <Paragraph>
                    If I click on the Pipeline Review card from the overview page, I will be dropped
                    into an easy-to-understand view of all my opportunities, displayed in table
                    format. Even better, I can easily adjust the columns that are shown in this
                    view. I don’t have to click through multiple layers of settings to make this
                    change, I can simply click Edit Columns button on the top-right of the screen,
                    and I can start adjusting my table view to fit my needs.
                </Paragraph>
                <Paragraph>
                    I want to find the highest potential value deals that have a close date within
                    the last month. To do that, I’ll edit my table view to include the Expected
                    Amount column:
                </Paragraph>
                <ImageWithCaption
                    src={image10ColumnSelection}
                    alt="Column selection for Pipeline Review"
                />
                <Paragraph>
                    Now, I can add a filter for the expected deal close date by clicking on the
                    Filters button at the top-right. You’ll notice there are already some filters in
                    place. I’m actually OK with those, so I’ll leave them alone. But I will click
                    the plus button next to those filters so I can add an additional filter. I’m
                    going to filter on Close Date:
                </Paragraph>
                <ImageWithCaption
                    src={image11TableFilter}
                    alt="Create filter for past close dates"
                />
                <Paragraph>
                    This shows that I have two opportunities that I should be looking at. One of
                    those opportunities has a much higher expected value than the other, so I know I
                    should be focusing on that:
                </Paragraph>
                <ImageWithCaption
                    src={image12Table}
                    alt="Table with Deals that have close dates in the past"
                />
                <Paragraph>
                    With just a couple of clicks, I’ve prioritized my day. Of course, in a fully
                    built out sales pipeline, you will surely have more than two deals matching
                    these criteria (or whatever criteria you set), and you can see how quickly being
                    able to filter on specific information can allow you to work faster, and it can
                    create more flexibility.
                </Paragraph>
                <Paragraph>
                    In a system where you have to set tasks based on a rigid system or framework, it
                    becomes hard to deviate from those tasks, even if there is a smarter way to
                    approach your prospect pipeline. Laserfocus cuts through the complexity and
                    gives you the agility needed to close more deals.
                </Paragraph>
                <Heading2>Wrapping Up</Heading2>
                <Paragraph>
                    You just learned how you can free yourself from the Salesforce experience
                    without actually leaving Salesforce. This is especially powerful in
                    organizations where you may have no choice but to use Salesforce or in
                    organizations where some people truly love Salesforce but others don’t.
                </Paragraph>
                <Paragraph>
                    Laserfocus gives you all the power you need to speed up your workflow and close
                    more deals while keeping the sales pipeline data in sync with Salesforce. If
                    you’re ready to see this in action, sign up today!
                </Paragraph>
            </TextSection>
        </PageLayout>
    )
}

export default SalesforceExperience
